import { Component, EventEmitter, Inject, Injector, Input, OnDestroy, OnInit, Output, ViewContainerRef } from '@angular/core';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';

import { NzMessageService } from 'ng-zorro-antd/message';
import { NzModalRef, NzModalService } from 'ng-zorro-antd/modal';

import { BehaviorSubject, Subscription } from 'rxjs';

import { ActionService, AuthService, cleanObject, CustomFormatter, ISitemap, ISitemapCommand, SITEMAP } from 'pbc.angular';

import { IPostSortiereAbschnitteRequest, IPostSortiereAbschnitteResponse } from 'fa-kt.types';
import { PostSortiereAbschnitteCommandService } from '../service';

@Component({
  selector: 'fa-kt-post-sortiere-abschnitte',
  templateUrl: './post-sortiere-abschnitte.component.html',
  styleUrls: ['./post-sortiere-abschnitte.component.css'],
})
export class PostSortiereAbschnitteCommandComponent implements OnInit, OnDestroy {
  command!: ISitemapCommand;

  subscriptions: Subscription[] = [];
  form!: FormGroup;
  $loading: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(false);
  $valid: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(false);
  CustomFormatter = CustomFormatter;

  @Input() set loading(loading: boolean) {
    this.$loading.next(this.$loading.getValue() || loading);
  }
  @Input() set value(value: IPostSortiereAbschnitteRequest) {
    this.patch(value);
  }
  @Output() valueChanged: EventEmitter<IPostSortiereAbschnitteRequest> = new EventEmitter<IPostSortiereAbschnitteRequest>();
  @Output() valid: EventEmitter<boolean> = new EventEmitter<boolean>();
  @Output() finished: EventEmitter<IPostSortiereAbschnitteResponse> = new EventEmitter<IPostSortiereAbschnitteResponse>();

  /* << fields    */
  /*    fields >> */

  get modalRef() {
    return this.injector.get(NzModalRef);
  }

  constructor(
    @Inject(SITEMAP) private sitemap: ISitemap,
    private fb: FormBuilder,
    private injector: Injector,
    private viewContainerRef: ViewContainerRef,
    private modal: NzModalService,
    private message: NzMessageService,
    private actions: ActionService,
    public auth: AuthService,
    public postSortiereAbschnitte: PostSortiereAbschnitteCommandService /* << more    */,
  ) /*    more >> */
  {
    this.command = sitemap.TEXTE.Commands.POST_SORTIERE_ABSCHNITTE;
    this.form = this.fb.group({
      abschnitte: this.fb.group({
        id: [null, []],
        vorlage: [null, [Validators.required]],
        platzhalter: [null, [Validators.required]],
        name: [null, [Validators.required]],
        text: [null, [Validators.required]],
      }),
    });
    /* << constructor    */
    /*    constructor >> */
    this.patch();
  }

  ngOnInit() {
    this.postSortiereAbschnitte.prepare().catch((e) => console.error(e));
    this.subscriptions.push(
      this.finished.subscribe((result) => {
        if (this.modalRef) {
          this.modalRef.destroy(result);
        }
      }),
      this.valid.subscribe((valid) => {
        if (valid) {
          this.actions.subscribe({ key: this.command.translation, action: async () => await this.submit() });
        } else {
          this.actions.unsubscribe(this.command.translation);
        }
      }),
      this.form.valueChanges.subscribe((value: IPostSortiereAbschnitteRequest) => {
        this.valueChanged.emit(value);
        this.valid.emit(this.form.valid);
      }),
      this.abschnitteId?.valueChanges.subscribe(async (id) => {
        /* << on-abschnitte-id-change    */
        /*    on-abschnitte-id-change >> */
      }) as Subscription,
      this.abschnitteVorlage?.valueChanges.subscribe(async (vorlage) => {
        /* << on-abschnitte-vorlage-change    */
        /*    on-abschnitte-vorlage-change >> */
      }) as Subscription,
      this.abschnittePlatzhalter?.valueChanges.subscribe(async (platzhalter) => {
        /* << on-abschnitte-platzhalter-change    */
        /*    on-abschnitte-platzhalter-change >> */
      }) as Subscription,
      this.abschnitteName?.valueChanges.subscribe(async (name) => {
        /* << on-abschnitte-name-change    */
        /*    on-abschnitte-name-change >> */
      }) as Subscription,
      this.abschnitteText?.valueChanges.subscribe(async (text) => {
        /* << on-abschnitte-text-change    */
        /*    on-abschnitte-text-change >> */
      }) as Subscription,
      /* << subscibe    */
      /*    subscibe >> */
    );
    /* << init    */
    /*    init >> */
  }

  ngOnDestroy() {
    /* << end    */
    /*    end >> */
    this.actions.unsubscribe(this.command.translation);
    this.subscriptions.filter(($: Subscription) => $ && !$.closed).forEach(($: Subscription) => $.unsubscribe());
  }

  private patch(value?: IPostSortiereAbschnitteRequest) {
    if (value) {
      value = cleanObject(value);
      if (value.abschnitte) {
        value.abschnitte = cleanObject(value.abschnitte);
      }
      this.form.patchValue(value);
    }
    this.update();
  }

  update() {
    this.$loading.next(true);
    this.form.markAsDirty();
    this.form.markAllAsTouched();
    this.form.updateValueAndValidity();
    this.$valid.next(this.form.valid);
    this.$loading.next(false);
  }

  async submit() {
    this.update();
    if (!this.form.valid) {
      return;
    }
    this.$loading.next(true);
    try {
      const payload = cleanObject(this.form.getRawValue());
      if (payload.abschnitte) {
        payload.abschnitte = cleanObject(payload.abschnitte);
      }
      const response: IPostSortiereAbschnitteResponse = await this.postSortiereAbschnitte.request(payload);
      this.finished.next(response);
    } catch (error: any) {
      this.message.error(error.message);
      for (const validation of error.errors ? error.errors : []) {
        for (const children of validation.children ? validation.children : []) {
          const message = Object.values(children.constraints).reduce((acc, val) => acc + ' ' + val, '');
          this.form.controls[validation.property].get(children.property)?.setErrors({ server: message });
        }
      }
    }
    this.$loading.next(false);
  }

  get abschnitteId() {
    return this.form.controls.abschnitte.get('id');
  }
  get abschnitteVorlage() {
    return this.form.controls.abschnitte.get('vorlage');
  }
  get abschnittePlatzhalter() {
    return this.form.controls.abschnitte.get('platzhalter');
  }
  get abschnitteName() {
    return this.form.controls.abschnitte.get('name');
  }
  get abschnitteText() {
    return this.form.controls.abschnitte.get('text');
  }
}
